import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play, MessageCircle, ArrowRight } from "lucide-react";
import loungeHero from "@/assets/lounge-hero.jpg.asset.json";
import cardHotelEvents from "@/assets/card-hotel-events-crop.jpg.asset.json";
import cardResidential from "@/assets/card-residential-new.jpg.asset.json";
import cardWedding from "@/assets/card-wedding.jpg.asset.json";
import cardFuneral from "@/assets/card-funeral.jpg.asset.json";
import { FaWhatsapp } from "react-icons/fa";
import { FaSpotify } from "react-icons/fa";
import Layout from "@/components/layout/Layout";
import OccasionDialog, { type Occasion } from "@/components/OccasionDialog";
import outlanderArt from "@/assets/outlander.png.asset.json";
import speakToMeArt from "@/assets/speak-to-me.png.asset.json";
import marthasHarbourArt from "@/assets/marthas-harbour.png.asset.json";
import abigailsSongArt from "@/assets/abigails-song.png.asset.json";
import alwaysYoursArt from "@/assets/always-yours.png.asset.json";
import singingSailorArt from "@/assets/singing-sailor.png.asset.json";
import bloodOfMyBloodArt from "@/assets/blood-of-my-blood.png.asset.json";
import ancientLoveArt from "@/assets/ancient-love.png.asset.json";
import sweetDecayArt from "@/assets/sweet-decay.png.asset.json";
import loveOfMineArt from "@/assets/love-of-mine.png.asset.json";

const singles = [
  { title: "Outlander", art: outlanderArt.url },
  { title: "Speak To Me", art: speakToMeArt.url },
  { title: "Martha's Harbour", art: marthasHarbourArt.url },
  { title: "Abigail's Song", art: abigailsSongArt.url },
];

// releaseDate drives the automatic "Out now" / "Coming soon" button (UK time, midnight)
const upcomingSingles = [
  { title: "Singing Sailor", date: "4 September 2026", releaseDate: new Date("2026-09-04T00:00:00+01:00"), art: singingSailorArt.url },
  { title: "Blood of My Blood", date: "18 September 2026", releaseDate: new Date("2026-09-18T00:00:00+01:00"), art: bloodOfMyBloodArt.url },
  { title: "Ancient Love", date: "2 October 2026", releaseDate: new Date("2026-10-02T00:00:00+01:00"), art: ancientLoveArt.url },
  { title: "Sweet Decay", date: "16 October 2026", releaseDate: new Date("2026-10-16T00:00:00+01:00"), art: sweetDecayArt.url },
  { title: "Love of Mine", date: "30 October 2026", releaseDate: new Date("2026-10-30T00:00:00+00:00"), art: loveOfMineArt.url },
  { title: "Always Yours", date: "16 November 2026", releaseDate: new Date("2026-11-16T00:00:00+00:00"), art: alwaysYoursArt.url },
];

const SPOTIFY_URL = "https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1";
import { WHATSAPP_URL } from "@/lib/whatsapp";
export const EMAIL = "hello@oliviaflemming.co.uk";

// Singles released before the Always Return album (May 2026)
const pastSingles = [
  { title: "Martha's Harbour", date: "8 May 2026", releaseDate: new Date("2026-05-08T00:00:00+01:00"), art: marthasHarbourArt.url, link: "https://open.spotify.com/album/3myLSxXK7brrRquilbKFSb" },
  { title: "Outlander", date: "14 May 2026", releaseDate: new Date("2026-05-14T00:00:00+01:00"), art: outlanderArt.url, link: "https://open.spotify.com/album/6O0L02oGgCtg4I6za4lzwj" },
  { title: "Speak To Me", date: "21 May 2026", releaseDate: new Date("2026-05-21T00:00:00+01:00"), art: speakToMeArt.url, link: "https://open.spotify.com/album/2Cz3vcGvhYy4yyn6RWsXph" },
  { title: "Abigail's Song", date: "28 May 2026", releaseDate: new Date("2026-05-28T00:00:00+01:00"), art: abigailsSongArt.url, link: "https://open.spotify.com/album/12RVdoJWdFegz76CvvnMtt" },
];

const releases = [
  ...upcomingSingles.map((s) => ({ ...s, kind: "Single", link: SPOTIFY_URL, text: "" })),
  ...pastSingles.map((s) => ({ ...s, kind: "Single", text: "" })),
  { title: "Always Return", date: "11 June 2026", releaseDate: new Date("2026-06-11T00:00:00+01:00"), art: "/images/album-artwork-600.webp", kind: "Album", link: "https://open.spotify.com/album/57iabyclor3as37i7vbqx4", text: "An album about home, belonging and the places that stay with us. For Olivia, that place is Spain." },
].map((r) => r.title === "Always Yours" ? { ...r, kind: "Album", text: "An album about love in all its forms: the love we find, lose, hold onto, and the love that stays with us." } : r)
  .sort((a, b) => b.releaseDate.getTime() - a.releaseDate.getTime());

const ReleaseTimeline = () => {
  const [open, setOpen] = useState(false);
  const shown = open ? releases : releases.slice(0, 3);
  return (
    <div className="max-w-3xl mx-auto mb-16">
      <p className="text-primary text-sm uppercase tracking-widest font-medium mb-2 text-center">All releases</p>
      <h3 className="text-3xl md:text-4xl font-playfair font-semibold text-white mb-10 text-center">Timeline</h3>
      <ol className="relative border-l border-primary/30 ml-3 space-y-10">
        {shown.map((r) => {
          const isOut = new Date() >= r.releaseDate;
          return (
            <li key={r.title} className="relative pl-8">
              <span className="absolute -left-[9px] top-8 w-4 h-4 rounded-full bg-primary" />
              <a href={r.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-6 group">
                <img src={r.art} alt={`${r.title} artwork`} loading="lazy" className="w-24 h-24 md:w-36 md:h-36 rounded-xl object-cover shrink-0" />
                <div className="min-w-0">
                  <p className="text-primary text-xs md:text-sm uppercase tracking-widest">{r.date} · {r.kind}</p>
                  <h4 className="text-xl md:text-3xl font-playfair text-white group-hover:text-primary transition-colors">{r.title}</h4>
                  {r.text && <p className="text-white/60 text-sm md:text-base leading-relaxed mt-1">{r.text}</p>}
                  <span className={`inline-block mt-2 text-[10px] uppercase tracking-widest ${isOut ? "text-primary" : "text-white/50"}`}>{isOut ? "Out now" : "Coming soon"}</span>
                </div>
              </a>
            </li>
          );
        })}
      </ol>
      {releases.length > 3 && (
        <div className="text-center mt-8">
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 border border-primary/40 text-primary px-6 py-2.5 rounded-full text-xs uppercase tracking-widest hover:border-primary transition-all">
            {open ? "Show less" : `Show all ${releases.length} releases`}
          </button>
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const reduceMotion = useReducedMotion();
  const [occasion, setOccasion] = useState<Occasion | null>(null);
  // Content is visible by default; a quick 300ms fade on mount, no scroll observer
  const fadeUp = reduceMotion
    ? { initial: false as const }
    : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };


  return (
    <Layout>
      {/* Hero */}
      <section id="hero" className="relative min-h-[100svh] bg-black overflow-hidden film-grain">
        <img
          src={loungeHero.url}
          alt="Olivia Flemming singing in a candlelit hotel lounge"
          className="absolute inset-0 w-full h-full object-cover object-[38%_center] md:-scale-x-100 md:object-[50%_28%]"
          loading="eager"

        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/40" />
        <div className="relative z-10 min-h-[100svh] max-w-6xl mx-auto px-6 flex items-center pt-24 pb-16">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-xl"
          >
            <p className="text-gold text-xs md:text-sm tracking-[0.35em] uppercase mb-6 font-playfair">
              Live vocal performer
            </p>
            <h1 className="text-6xl md:text-8xl font-playfair font-normal text-white leading-[0.95] tracking-tight mb-8">
              OLIVIA<br />FLEMMING
            </h1>
            <div className="w-12 h-px bg-gold/70 mb-8" />
            <p className="text-white/85 text-lg md:text-2xl font-playfair italic leading-relaxed mb-10">
              Atmospheric, elegant and unforgettable live music for exceptional venues and special moments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-cherry/80 border border-gold/50 text-white px-8 py-3.5 rounded-full text-xs tracking-[0.25em] uppercase whitespace-nowrap hover:bg-cherry transition-all"
              >
                Message me <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => scrollTo("music")}
                className="flex items-center justify-center gap-3 border border-gold/50 text-white px-8 py-3.5 rounded-full text-xs tracking-[0.25em] uppercase whitespace-nowrap hover:border-gold hover:text-gold transition-all"
              >
                Listen to my music <Play className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <OccasionDialog occasion={occasion} onClose={() => setOccasion(null)} />
      {/* Venue cards */}
      <section id="occasions" className="bg-background py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-6">
          {[
            { img: cardHotelEvents.url, title: "Hotels & Events", sub: "Luxury live music for unforgettable occasions", id: "contact", popup: "hotels" as Occasion },
            { img: cardResidential.url, title: "Residential Homes & Retirement Villages", sub: "Relaxed, uplifting performances tailored to your residents", id: "contact", popup: "residential" as Occasion },
            { img: cardWedding.url, title: "Weddings", sub: "Timeless songs for your ceremony and your day", id: "contact", pos: "object-[50%_18%]", popup: "weddings" as Occasion },
            { img: cardFuneral.url, title: "Funerals", sub: "Gentle, heartfelt music to say goodbye", id: "contact", popup: "funerals" as Occasion },
          ].map((c) => (
            <button
              key={c.title}
              onClick={() => setOccasion(c.popup)}
              className="group relative h-80 md:h-96 overflow-hidden border border-gold/25 text-center"
            >
              <img src={c.img} alt="" width={1024} height={768} loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${c.pos || ""} transition-transform duration-700 group-hover:scale-105`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/35" />
              <div className="relative h-full flex flex-col items-center justify-end p-8 gap-3">
                <h2 className="font-playfair text-2xl md:text-3xl uppercase tracking-wide text-gold">{c.title}</h2>
                <p className="text-white/80 text-xs tracking-[0.2em] uppercase max-w-sm">{c.sub}</p>
                <span className="mt-3 inline-flex items-center gap-2 border border-gold/50 text-white px-6 py-2.5 rounded-full text-xs tracking-[0.25em] uppercase group-hover:border-gold group-hover:text-gold transition-colors">
                  Find out more <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-16 md:py-24 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div {...fadeUp} className="grid md:grid-cols-[2fr_3fr] gap-10 md:gap-16 items-center">
            <div className="aspect-[4/5] overflow-hidden border border-gold/25">
              <img
                src={speakToMeArt.url}
                alt="Olivia Flemming portrait"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="space-y-6">
              <p className="text-gold text-xs tracking-[0.35em] uppercase font-playfair">About</p>
              <h2 className="text-3xl md:text-5xl font-playfair font-normal text-white tracking-wide">
                OLIVIA FLEMMING
              </h2>
              <div className="w-12 h-px bg-gold/70" />
              <p className="text-white/75 leading-relaxed text-lg font-playfair">
                Classically trained with a love for cinematic, atmospheric and timeless music, Olivia performs a unique blend of classical, contemporary and original songs. Her performances create a beautiful and memorable atmosphere for any venue, whether it's a luxury hotel, private event or a residential home.
              </p>
              <p className="text-white/60 leading-relaxed">
                Birmingham based live vocalist bringing elegant, atmospheric music to weddings, hotels, care homes and special occasions across the West Midlands.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Music / Releases */}
      <section id="music" className="py-16 md:py-32 bg-cream">
        <div className="max-w-6xl mx-auto px-3 md:px-4">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-5xl font-playfair font-semibold text-white text-center mb-10 md:mb-16">
              Music
            </h2>

            <div className="space-y-12 md:space-y-20 mb-16 md:mb-24">
              {releases.filter((r) => r.kind === "Album").map((r, i) => {
                const isOut = new Date() >= r.releaseDate;
                return (
                  <div key={r.title} className={`grid md:grid-cols-2 gap-6 md:gap-12 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                    <img src={r.art} alt={`${r.title} album artwork`} loading="lazy" className="w-full aspect-square object-cover rounded-xl border border-gold/25 shadow-2xl" />
                    <div className="space-y-4 px-1">
                      <p className="text-primary text-xs uppercase tracking-[0.3em]">{isOut ? "Album · Out now" : "New album · Coming soon"}</p>
                      <h3 className="text-4xl md:text-6xl font-playfair text-white">{r.title}</h3>
                      <p className="text-white/50 text-sm uppercase tracking-widest">{r.date}</p>
                      <p className="text-white/75 text-lg leading-relaxed">{r.text}</p>
                      <a href={r.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-primary/90 transition-all">
                        <FaSpotify className="w-4 h-4" /> {isOut ? "Listen on Spotify" : "Follow on Spotify"}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            <ReleaseTimeline />


            <div className="text-center mb-6">
              <p className="text-primary text-sm uppercase tracking-widest font-medium mb-2">Listen on Spotify</p>
              <h3 className="text-2xl md:text-3xl font-playfair font-semibold text-white">
                Top Tracks
              </h3>
            </div>
            <div className="bg-muted rounded-2xl p-2 overflow-hidden">
              <iframe
                title="Olivia Flemming on Spotify"
                src="https://open.spotify.com/embed/artist/3iP5TKieYruTr7XClnpjX1?utm_source=generator&theme=0"
                width="100%"
                height="600"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-xl"
              />
            </div>
            <div className="text-center mt-6">
              <a
                href="https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-black px-6 py-3 rounded-full font-medium text-sm hover:bg-primary/90 transition-all"
              >
                <FaSpotify className="w-4 h-4" />
                Follow on Spotify
              </a>
            </div>
          </motion.div>
        </div>
      </section>



      {/* Visual Gallery — Single Artworks */}
      <section id="gallery" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div {...fadeUp}>
            <p className="text-primary text-sm uppercase tracking-widest font-medium text-center mb-3">Singles on Spotify</p>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white text-center mb-16">
              Visuals
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {[...singles, ...upcomingSingles].filter(({ title }) => title !== "Always Yours").map(({ title, art }) => (
                <a
                  key={title}
                  href={SPOTIFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative aspect-square rounded-xl overflow-hidden bg-muted"
                >
                  <img
                    src={art}
                    alt={`${title}. Olivia Flemming single artwork`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="text-white font-playfair text-lg leading-tight">{title}</p>
                    <p className="flex items-center gap-1.5 text-primary text-xs mt-1">
                      <FaSpotify className="w-3 h-3" /> Listen on Spotify
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>




      {/* Contact */}
      <section id="contact" className="py-20 md:py-32 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white mb-6">
              Let's Create Something Together
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
              Open to collaborations, features, and creative projects. Let's talk.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-primary text-black px-8 py-4 rounded-full font-semibold text-base hover:bg-primary/90 transition-all"
            >
              <FaWhatsapp className="w-5 h-5" />
              Message me on WhatsApp
            </a>
            <p className="text-white/60 mt-6">
              Or email{" "}
              <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
                {EMAIL}
              </a>
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
